import React, { useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Save, RefreshCw, Plus, Users, Image, Edit3 } from 'lucide-react';
import { ThemeContext } from '../../constant/theme';
import { LanguageContext } from '../../constant/LanguageContext';
import { text, translateMessage } from './constants/configurationText';

import { useCompanies } from './hooks/useCompanies';
import { usePermissions } from './hooks/usePermissions';
import { useSplash } from './hooks/useSplash';
import { useAssignAdmin } from './hooks/useAssignAdmin';
import { useCodePolicy } from './hooks/useCodePolicy';
import { getStyles, getConfigurationGlobalCss } from './styles/configurationStyles';

import CompanyList from './components/CompanyList';
import ModulePermissions from './components/ModulePermissions';
import WorkingCompanySelector from './components/WorkingCompanySelector';
import { useWorkingCompanyModules } from './hooks/useWorkingCompanyModules';
import CompanyStatusToggle from './components/CompanyStatusToggle';
import SplashSection from './components/SplashSection';
import SubscriptionDetails from './components/SubscriptionDetails';
import CodePolicySection from './components/CodePolicySection';
import AddCompanyModal from './components/AddCompanyModal';
import RenameCompanyModal from './components/RenameCompanyModal';
import AssignAdminModal from './components/AssignAdminModal';
import MessageAlert from './components/MessageAlert';
import RemotePunchSettings from './components/RemotePunchSettings';
import { SkeletonStyles, SkeletonBox, LoadingPill } from '../../components/SkeletonLoader';

const ConfigurationManagement = () => {
  const { theme } = useContext(ThemeContext);
  const { language: rawLanguage } = useContext(LanguageContext) || { language: 'en' };
  const language = rawLanguage === 'ar' ? 'ar' : 'en';
  const isRTL = language === 'ar';
  const t = text[language];
  const navigate = useNavigate();
  const splashSectionRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  }, []);

  const { companies, loading: loadingCompanies, fetchCompanies, addCompany, renameCompany } = useCompanies(showMessage);

  const {
    permissions,
    selectedCompany,
    loading: loadingPermissions,
    saving,
    fetchCompanyPermissions,
    handleSave,
    handleToggle,
    handlePermissionChange,
  } = usePermissions(showMessage, fetchCompanies);

  const {
    splashInfo,
    loadingSplash,
    uploadingSplash,
    splashUrlInput,
    setSplashUrlInput,
    splashFile,
    splashPreviewUrl,
    splashActive,
    iconUrlInput,
    setIconUrlInput,
    iconFile,
    iconPreviewUrl,
    setIconPreviewUrl,
    iconRemoved,
    removeIcon,
    fetchSplashData,
    handleSplashFileChange,
    clearSplashFile,
    handleIconFileChange,
    clearIconFile,
    handleSplashSave,
    handleSplashDeactivate,
    toggleSplashActive,
  } = useSplash(showMessage);

  const {
    showAssignAdminModal,
    adminCandidates,
    selectedAdminIds,
    adminSearch,
    setAdminSearch,
    loadingAdmins,
    openAssignAdmin,
    handleToggleAdmin,
    handleSaveAdmins,
    closeAssignAdminModal,
  } = useAssignAdmin(showMessage);

  const {
    policy: codePolicy,
    draft: codePolicyDraft,
    loading: loadingCodePolicy,
    saving: savingCodePolicy,
    isDirty: codePolicyDirty,
    previewCodes: codePolicyPreview,
    previewLoading: codePolicyPreviewLoading,
    previewError: codePolicyPreviewError,
    warnings: codePolicyWarnings,
    fetchCodePolicy,
    updateDraft: updateCodePolicyDraft,
    resetDraft: resetCodePolicyDraft,
    previewDraft: previewCodePolicyDraft,
    saveCodePolicy,
  } = useCodePolicy(showMessage);

  const styles = getStyles(theme);
  const loading = loadingCompanies || loadingPermissions;

  // ── Initial-load skeleton — only until the company list first arrives ────────
  const [pageReady, setPageReady] = useState(false);
  React.useEffect(() => {
    if (!loadingCompanies) setPageReady(true);
  }, [loadingCompanies]);

  const {
    companies: workingCompanies,
    selectedWorkingCompany,
    selectedEntry: selectedWorkingCompanyEntry,
    draft: workingCompanyDraft,
    loadingWorkingCompanies,
    savingWorkingCompany,
    fetchWorkingCompanies,
    selectWorkingCompany,
    toggleDraft: toggleWorkingCompanyDraft,
    saveWorkingCompany,
    resetWorkingCompany,
  } = useWorkingCompanyModules(showMessage);

  const handleCompanySelect = (clientName) => {
    fetchCompanyPermissions(clientName, (name) => fetchSplashData(name));
    fetchCodePolicy(clientName);
    fetchWorkingCompanies(clientName);
  };

  // Saving the client row changes the default every non-custom working
  // company inherits — refresh their effective values afterwards.
  const handleSaveClientDefault = async () => {
    await handleSave();
    fetchWorkingCompanies(selectedCompany, true);
  };
  const isSavingAny = saving || savingWorkingCompany;

  // Renames the currently-selected client, then reselects it under the new
  // name so the whole panel (permissions, splash, code policy) reloads
  // scoped to it — the rename already cascaded server-side to every table
  // (see useCompanies.renameCompany / configurationService.renameClient).
  const handleRenameCompany = async (oldName, newName) => {
    const success = await renameCompany(oldName, newName);
    if (success) {
      handleCompanySelect(newName.trim());
    }
    return success;
  };

  const globalCss = getConfigurationGlobalCss(theme);

  const header = (
    <div className="nx-page-header" style={styles.header}>
      <div style={styles.headerText}>
        <h1 className="nx-page-title" style={styles.pageTitle}>
          <Settings size={28} />
          {t.title}
        </h1>
        <p style={styles.pageSubtitle}>{t.subtitle}</p>
      </div>
      <button className="cfg-btn-primary" style={styles.btnPrimary()} onClick={() => setShowAddModal(true)}>
        <Plus size={16} />
        {t.addCompany}
      </button>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the company list first loads
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && loadingCompanies) {
    return (
      <div className="cfg-scope nx-flush-mobile" style={styles.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill text={t.loadingPage} theme={theme} isRTL={isRTL} />

        <div style={{ ...styles.header, animation: 'none' }}>
          <div style={styles.headerText}>
            <SkeletonBox width="260px" height="30px" style={{ marginBottom: '12px' }} />
            <SkeletonBox width="320px" height="16px" style={{ maxWidth: '100%' }} />
          </div>
          <SkeletonBox width="150px" height="44px" style={{ borderRadius: '12px' }} />
        </div>

        <div className="responsive-two-col" style={styles.gridContainer}>
          <div style={styles.card}>
            <SkeletonBox width="140px" height="22px" style={{ marginBottom: '20px' }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonBox key={i} width="100%" height="64px" style={{ borderRadius: '12px', marginBottom: '8px' }} />
            ))}
          </div>
          <div style={styles.permissionsContainer}>
            <SkeletonBox width="220px" height="24px" style={{ marginBottom: '20px' }} />
            {[1, 2, 3, 4].map((i) => (
              <SkeletonBox key={i} width="100%" height="46px" style={{ borderRadius: '10px', marginBottom: '10px' }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cfg-scope nx-flush-mobile" style={styles.container}>
      <style>{globalCss}</style>

      {showAddModal && (
        <AddCompanyModal
          theme={theme}
          styles={styles}
          t={t}
          onAdd={addCompany}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showRenameModal && (
        <RenameCompanyModal
          theme={theme}
          styles={styles}
          t={t}
          currentName={selectedCompany}
          onRename={handleRenameCompany}
          onClose={() => setShowRenameModal(false)}
        />
      )}

      {showAssignAdminModal && (
        <AssignAdminModal
          theme={theme}
          styles={styles}
          t={t}
          selectedCompany={selectedCompany}
          adminCandidates={adminCandidates}
          selectedAdminIds={selectedAdminIds}
          adminSearch={adminSearch}
          loadingAdmins={loadingAdmins}
          setAdminSearch={setAdminSearch}
          onToggleAdmin={handleToggleAdmin}
          onSave={() => handleSaveAdmins(selectedCompany)}
          onClose={closeAssignAdminModal}
        />
      )}

      {header}

      <MessageAlert message={{ ...message, text: translateMessage(message.text, language) }} styles={styles} />

      {loading && !permissions ? (
        <div style={{ ...styles.card, ...styles.emptyState }}>
          <RefreshCw size={56} style={{ marginBottom: '20px', opacity: 0.3, animation: 'spin 2s linear infinite' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: styles.colors.body }}>{t.loading}</h3>
        </div>
      ) : (
        <div className="responsive-two-col" style={styles.gridContainer}>
          {/* Company List */}
          <CompanyList
            companies={companies}
            selectedCompany={selectedCompany}
            theme={theme}
            styles={styles}
            t={t}
            language={language}
            onSelect={handleCompanySelect}
          />

          {/* Permissions Panel */}
          {!permissions ? (
            <div style={{ ...styles.permissionsContainer, ...styles.emptyState, minHeight: 0 }}>
              <Settings size={56} style={{ marginBottom: '20px', opacity: 0.3 }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 8px', color: styles.colors.body }}>
                {t.selectCompany}
              </h3>
              <p style={{ margin: 0, fontSize: '14px' }}>{t.selectCompanyHint}</p>
            </div>
          ) : (
            <div style={styles.permissionsContainer}>
              {/* Company Header */}
              <div style={styles.panelHeader}>
                <div style={styles.panelAvatar}>
                  {selectedCompany?.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={styles.panelTitle}>{selectedCompany}</h3>
                  <p style={styles.panelSubtitle}>{t.configureModules}</p>
                </div>
                <button
                  className="cfg-btn-secondary"
                  style={styles.btnSecondary()}
                  onClick={() => setShowRenameModal(true)}
                  title={t.renameTooltip}
                >
                  <Edit3 size={15} />
                  {t.rename}
                </button>
                <button
                  className="cfg-btn-primary"
                  style={styles.btnPrimary(isSavingAny)}
                  onClick={selectedWorkingCompany ? () => saveWorkingCompany(selectedCompany) : handleSaveClientDefault}
                  disabled={isSavingAny}
                >
                  {isSavingAny
                    ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Save size={15} />}
                  {isSavingAny ? t.saving : selectedWorkingCompany ? `${t.save} — ${selectedWorkingCompany}` : t.save}
                </button>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
                {[
                  { label: t.createAdmin, icon: <Users size={14} />, onClick: () => navigate('/DashboardSuper/AdminAccountCreation') },
                  { label: t.assignAdmin, icon: <Users size={14} />, onClick: () => openAssignAdmin(selectedCompany) },
                  { label: t.splashScreen, icon: <Image size={14} />, onClick: () => splashSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
                ].map(btn => (
                  <button key={btn.label} className="cfg-action-btn" style={styles.actionBtn} onClick={btn.onClick}>
                    {btn.icon}
                    {btn.label}
                  </button>
                ))}
              </div>

              {/* Splash Section */}
              <SplashSection
                theme={theme}
                styles={styles}
                t={t}
                selectedCompany={selectedCompany}
                splashInfo={splashInfo}
                loadingSplash={loadingSplash}
                uploadingSplash={uploadingSplash}
                splashPreviewUrl={splashPreviewUrl}
                splashUrlInput={splashUrlInput}
                setSplashUrlInput={setSplashUrlInput}
                splashActive={splashActive}
                iconPreviewUrl={iconPreviewUrl}
                iconUrlInput={iconUrlInput}
                setIconUrlInput={setIconUrlInput}
                setIconPreviewUrl={setIconPreviewUrl}
                iconFile={iconFile}
                splashFile={splashFile}
                splashSectionRef={splashSectionRef}
                onSplashFileChange={handleSplashFileChange}
                onClearSplash={clearSplashFile}
                onIconFileChange={handleIconFileChange}
                onClearIcon={clearIconFile}
                iconRemoved={iconRemoved}
                onRemoveIcon={removeIcon}
                onSave={() => handleSplashSave(selectedCompany)}
                onDeactivate={() => handleSplashDeactivate(selectedCompany)}
                onToggleActive={toggleSplashActive}
              />

              {/* Which working company the module switches below apply to */}
              <WorkingCompanySelector
                theme={theme}
                companies={workingCompanies}
                selected={selectedWorkingCompany}
                selectedEntry={selectedWorkingCompanyEntry}
                loading={loadingWorkingCompanies}
                saving={savingWorkingCompany}
                onSelect={selectWorkingCompany}
                onReset={() => resetWorkingCompany(selectedCompany)}
              />

              {/* Client-level sections — hidden while a working company is selected */}
              {!selectedWorkingCompany && (
              <SubscriptionDetails
                theme={theme}
                styles={styles}
                t={t}
                permissions={permissions}
                onChange={handlePermissionChange}
              />
              )}

              {/* Infinity Code Format */}
              {!selectedWorkingCompany && (
              <CodePolicySection
                theme={theme}
                styles={styles}
                t={t}
                clientName={selectedCompany}
                policy={codePolicy}
                draft={codePolicyDraft}
                loading={loadingCodePolicy}
                saving={savingCodePolicy}
                isDirty={codePolicyDirty}
                previewCodes={codePolicyPreview}
                previewLoading={codePolicyPreviewLoading}
                previewError={codePolicyPreviewError}
                warnings={codePolicyWarnings}
                onUpdateDraft={updateCodePolicyDraft}
                onReset={resetCodePolicyDraft}
                onPreview={previewCodePolicyDraft}
                onSave={saveCodePolicy}
              />
              )}

              {/* Remote Punch — per company; remounts on company switch */}
              <RemotePunchSettings key={selectedCompany} theme={theme} styles={styles} clientName={selectedCompany} />

              {/* Company Status */}
              {!selectedWorkingCompany && (
              <CompanyStatusToggle
                theme={theme}
                permissions={permissions}
                styles={styles}
                t={t}
                onToggle={handleToggle}
              />
              )}

              {/* Module Permissions — client default, or the selected working company's own */}
              <ModulePermissions
                permissions={selectedWorkingCompany ? workingCompanyDraft : permissions}
                styles={styles}
                theme={theme}
                t={t}
                onToggle={selectedWorkingCompany ? toggleWorkingCompanyDraft : handleToggle}
                onlyCategories={selectedWorkingCompany ? ['Modules'] : null}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigurationManagement;
